/**
 * Exports on a phone (mobile M3).
 *
 * In a browser an export is a plain `<a href download>`: the browser fetches
 * it with the session cookie and saves it. Inside the Android app neither half
 * works — the app has no cookie (it signs in with a token) and the WebView has
 * no download manager. So on native the file is fetched here with the app's
 * own headers, written to the app's cache, and handed to Android's share
 * sheet, where "Save to Files", Drive or a chat are one tap away.
 *
 * The cache is the only place written to (the FileProvider shares nothing
 * else — `res/xml/file_paths.xml`), and Android clears it on its own.
 */

import { apiBase, isNative } from "./index";
import { nativeRequestHeaders } from "./session";

/** Whether exports should go through `shareExport` rather than a plain link. */
export function exportsViaShareSheet(): boolean {
  return isNative();
}

function toBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Fetch `path` (an `/api/...` export) and offer it through the share sheet.
 *  Throws with a readable message, for the caller's toast. */
export async function shareExport(path: string, filename: string): Promise<void> {
  const res = await fetch(apiBase() + path, { headers: nativeRequestHeaders() });
  if (!res.ok) throw new Error(`The export failed (${res.status}).`);
  const data = toBase64(await res.arrayBuffer());

  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import("@capacitor/filesystem"),
    import("@capacitor/share"),
  ]);
  const written = await Filesystem.writeFile({ path: filename, data, directory: Directory.Cache });
  await Share.share({ title: filename, files: [written.uri], dialogTitle: "Save or send" });
}

/* ------------------------------------------------- files shared into the app (M7) */

/** A file another app shared into MyDeck, already copied into the app's cache. */
export interface SharedFile {
  name: string;
  mimeType: string;
  read: () => Promise<File>;
}

type CapacitorFiles = { convertFileSrc?: (path: string) => string };

/**
 * Listen for files shared into the Android app (the manifest offers MyDeck
 * for PDFs, and octet-stream). Registered once at boot: the plugin keeps a
 * share that cold-started the app until the first listener arrives.
 */
export function onFilesShared(receive: (files: SharedFile[]) => void): void {
  if (!isNative()) return;
  void import("@capgo/capacitor-share-target").then(({ CapacitorShareTarget }) =>
    CapacitorShareTarget.addListener("shareReceived", ({ files }) => {
      const convert = (window as Window & { Capacitor?: CapacitorFiles }).Capacitor?.convertFileSrc;
      if (!convert || !files?.length) return;
      receive(
        files.map((f) => ({
          name: f.name,
          mimeType: f.mimeType,
          // Served back to the WebView from the app's own cache through Capacitor's local file route.
          read: async () => {
            const res = await fetch(convert(f.uri));
            if (!res.ok) throw new Error("The shared file couldn't be read.");
            return new File([await res.blob()], f.name, { type: f.mimeType });
          },
        })),
      );
    }),
  );
}
